import cx from 'clsx';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { type ModalOverlayProps as AriaModalProps, Dialog, Modal } from 'react-aria-components';
import type { AriaDialogProps } from 'react-aria';
import { useIntl } from 'react-intl';
import { CloseIcon } from '#assets/icons/close-icon';
import { Badge, type BadgeProps } from '#components/badge/badge';
import { Button } from '#components/button/button';
import styles from './popup.module.css';
import { PopupBadge } from './related/popup-badge';
import { PopupBody } from './related/popup-body';
import { PopupCloseButton } from './related/popup-close-button';
import { PopupFooter } from './related/popup-footer';
import { PopupHeader } from './related/popup-header';
import { PopupIllustration } from './related/popup-illustration';
import { PopupOverlayRoot } from './related/popup-overlay-root';
import { PopupRoot } from './related/popup-root';
import { PopupSubtitle } from './related/popup-subtitle';
import { PopupTitle } from './related/popup-title';

interface BaseProps extends AriaModalProps, AriaDialogProps {
  title: string;
  subtitle?: string;
  illustration?: ReactNode;
  badge?: BadgeProps;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  closeClassName?: string;
  confirmActionLoading?: boolean;
}

interface NormalPopupProps extends BaseProps {
  type?: 'confirm' | 'destructive';
  onCustomCancelCta?: never;
}

interface PromotionalPopupProps extends BaseProps {
  type: 'promotional';
  onCustomCancelCta?: () => void; // TODO: Remove this prop when the Regate integration is complete.
}

interface InfoPopupProps extends BaseProps {
  type: 'info';
  onConfirm?: never;
  confirmText?: never;
  cancelText?: never;
  footerClassName?: never;
  onCustomCancelCta?: never;
}

export type PopupProps = PropsWithChildren<
  NormalPopupProps | PromotionalPopupProps | InfoPopupProps
>;

export function PopupComponent({
  title,
  subtitle,
  type = 'confirm',
  illustration,
  badge,
  onConfirm,
  onOpenChange,
  onCancel = () => {
    onOpenChange?.(false);
  },
  confirmText,
  cancelText,
  onCustomCancelCta,
  children,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  closeClassName,
  confirmActionLoading,
  ...props
}: PopupProps): ReactElement {
  const { formatMessage } = useIntl();
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...restProps } = props;

  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  return (
    <Modal
      {...restProps}
      UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
      className={cx(styles.wrapper, className)}
    >
      <Dialog aria-label={ariaLabel} aria-labelledby={ariaLabelledBy} className={styles.dialog}>
        <Button
          aria-label={formatMessage({ id: 'a11y-buttons-close-aria-label' })}
          className={cx(
            styles.close,
            closeClassName,
            illustration ? styles.withIllustration : null
          )}
          data-testid="modal-close-button"
          iconOnly
          onPress={onCancel}
          type="button"
          variant="tertiary"
        >
          <CloseIcon aria-hidden />
        </Button>
        {illustration ? (
          <div className={cx(type === 'promotional' && styles.promotionalIllustration)}>
            {illustration}
          </div>
        ) : null}
        {badge ? (
          <div className={styles.badge}>
            <Badge {...badge} />
          </div>
        ) : null}
        <div className={cx(styles.header, headerClassName)}>
          <h1 className={cx(styles.title, 'heading-section')} data-testid="modal-title">
            {title}
          </h1>
          {Boolean(subtitle) && (
            <h2 className={cx(styles.subtitle, 'body-standard')} data-testid="modal-description">
              {subtitle}
            </h2>
          )}
        </div>
        {Boolean(children) && <div className={cx(styles.body, bodyClassName)}>{children}</div>}
        {type !== 'info' && (
          <div className={cx(styles.footer, footerClassName)}>
            {Boolean(cancelText) && (
              <Button
                data-testid="modal-cancel-button"
                onPress={type === 'promotional' && onCustomCancelCta ? onCustomCancelCta : onCancel}
                variant="secondary"
              >
                {cancelText}
              </Button>
            )}
            <Button
              data-testid="modal-confirm-button"
              isLoading={confirmActionLoading}
              onPress={onConfirm}
              variant={type === 'destructive' ? 'danger' : 'primary'}
            >
              {confirmText}
            </Button>
          </div>
        )}
      </Dialog>
    </Modal>
  );
}

export const Popup = Object.assign(PopupComponent, {
  Root: PopupRoot,
  OverlayRoot: PopupOverlayRoot,
  CloseButton: PopupCloseButton,
  Illustration: PopupIllustration,
  Badge: PopupBadge,
  Header: PopupHeader,
  Title: PopupTitle,
  Subtitle: PopupSubtitle,
  Body: PopupBody,
  Footer: PopupFooter,
});
